import React, { useEffect, useState } from "react";
import ContractualRevenues from "./ContractualRevenues";
import WbsStructure from "./WbsStructure";
import DocumentsArchive from "./DocumentsArchive";
import { Col, Row, Typography, Collapse } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { cloneDeep, difference, get, isEmpty, remove } from "lodash";
import { CheckOutlined, RollbackOutlined } from "@ant-design/icons";
import Button from "../../../../../components/functional_components/Button/PFButton";
import ProjectCostPlan from "../../../../../services/api/ProjectCostPlan";
import { checkRoles, showError, showSuccess } from "../../../../../utils/utilities";
import ImportPlanning from "../../../../../services/api/ImportPlanning";
import { setLoading } from "../../../../../redux/ducks/Loading";
import Project from "../../../../../services/api/Project";
import { setIfClosed, setOpenPlanning, setProject } from "../../../../../redux/ducks/ProjectManagement";
import { useParams } from "react-router-dom";

const { Text } = Typography;

const ProjectsForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const projectManagement = useSelector((state) => get(state.ProjectManagementDuck, "project", {}));
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const [state, setState] = useState({
    projectData: [],
    infoProjectMap: [],
    downloadLoading: false,
    closed: null,
    panelActiveKeys: [],
  });

  useEffect(() => {
    getData();
  }, [projectManagement]);

  const getData = async () => {
    let stateOfSale = null;
    switch (projectManagement.clientPortalType) {
      case "CONTROLLING_COMPANY":
        stateOfSale = t("client.clientDetail.typeOptions.parentCompany");
        break;
      case "CONTROLLED_COMPANY":
        stateOfSale = t("client.clientDetail.typeOptions.subsidiaryCompany");
        break;
      case "RELATED_COMPANY":
        stateOfSale = t("client.clientDetail.typeOptions.associatedCompany");
        break;
      case "THIRD_PARTY":
        stateOfSale = t("client.clientDetail.typeOptions.thirdPart");
        break;
      default:
        stateOfSale = projectManagement.clientPortalType;
    }
    if (
      checkRoles(userInfo.roles, ["CONTROLLER", "SYSTEM_IT", "PM", "SUPPORT_PM", "SUPER_PM"]) &&
      state.closed === null
    ) {
      const response = await ImportPlanning.lastFreeze();
      if (!response.error) {
        dispatch(setOpenPlanning(get(response, "data.openPlanning", null)));
        dispatch(setIfClosed(get(response, "data.closed", null)));
        setState((prevState) => ({ ...prevState, closed: response.data.closed }));
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
    }
    setState((prevState) => ({
      ...prevState,
      projectData: projectManagement,
      infoProjectMap: [
        {
          name: "legalEntity",
          value: projectManagement.society,
        },
        {
          name: "costCentre",
          value: `${get(projectManagement, "costCentre.id", "-")} | ${get(projectManagement, "costCentre.name", "-")}`,
        },
        {
          name: "project",
          value: `${get(projectManagement, "id", "-")} | ${get(projectManagement, "name", "-")}`,
        },
        {
          name: "initiative",
          value: projectManagement.initiative,
        },
        {
          name: "client",
          value: `${get(projectManagement, "client.id", "-")} | ${get(projectManagement, "client.name", "-")}`,
        },
        {
          name: "salesforceID",
          value: projectManagement.salesforceId,
        },
        {
          name: "segment",
          value: `${get(projectManagement, "costCentre.segment.id", "-")} | ${get(
            projectManagement,
            "costCentre.segment.name",
            "-"
          )}`,
        },
        {
          name: "programManager",
          value: `${get(projectManagement, "userPm.name", "-")} ${get(projectManagement, "userPm.surname", "-")}`,
        },
        {
          name: "profitCenter",
          value: `${get(projectManagement, "costCentre.profitCentre.id", "-")} | ${get(
            projectManagement,
            "costCentre.profitCentre.name",
            "-"
          )}`,
        },
        {
          name: "projectEstimate",
          value: projectManagement.projectEstimateId,
        },
        {
          name: "clientSN",
          value: `${get(projectManagement, "clientPortalId", "-")} | ${get(
            projectManagement,
            "clientPortalName",
            "-"
          )}`,
        },
        {
          name: "vertical",
          value: projectManagement.verticalId,
        },
        {
          name: "customerType",
          value: stateOfSale,
        },
        {
          name: "projectStatus",
          value: projectManagement.status,
        },
        {
          name: "stateOfSale",
          value: projectManagement.projectEstimateStatusId,
        },
        {
          name: "paymentTerms",
          value: projectManagement.paymentTerms,
        },
        {
          name: "supportPM",
          value: `${get(projectManagement, "userSupportPm.name", "-")} ${get(
            projectManagement,
            "userSupportPm.surname",
            "-"
          )}`,
        },
        {
          name: "currency",
          value: projectManagement.currency,
        },
      ],
    }));
  };

  const projectDetail = async (id) => {
    dispatch(setLoading(true));
    const infoProject = await Project.getInfoProject(id);
    if (!infoProject.error) {
      dispatch(setProject(get(infoProject, "data", {})));
    } else {
      showError(get(infoProject, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const handleReopen = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const response = await ProjectCostPlan.reopenProjectCostPlan(projectManagement.id);
    if (!response.error) {
      showSuccess();
      projectDetail(params.id);
    } else {
      showError(get(response, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false, panelActiveKeys: [] }));
  };

  const changeActivePanel = (keys) => {
    let activePanel = cloneDeep(state.panelActiveKeys);
    const diff = difference(activePanel, keys);
    if (isEmpty(diff)) {
      activePanel = keys;
    } else {
      remove(activePanel, (k) => {
        return k === diff[0];
      });
    }
    setState((prevState) => ({ ...prevState, panelActiveKeys: activePanel }));
  };

  const items = [
    {
      key: 1,
      label: t("projectsManagement.ProjectForm.contractualRevenuesTitle"),
      className: "mb-1em",
      children: <ContractualRevenues />,
    },
    {
      key: 2,
      label: t("projectsManagement.ProjectForm.wbsStructureTitle"),
      className: "mb-1em",
      children: <WbsStructure />,
    },
    {
      key: 3,
      label: t("projectsManagement.ProjectForm.documentsArchiveTitle"),
      className: "mb-1em",
      children: <DocumentsArchive />,
    },
  ];

  return (
    <>
      {checkRoles(userInfo.roles, ["CONTROLLER", "SYSTEM_IT"]) && (
        <Row type="flex" align="middle" justify="end" className="mb-10">
          <Button
            isLoading={state.downloadLoading}
            isDisabled={state.closed}
            currentIcon={<RollbackOutlined className="icon-medium" />}
            clickCallback={handleReopen}
          />
        </Row>
      )}
      <Row>
        {state.infoProjectMap.map((item, i) => (
          <Col key={item.name} offset={i % 2 !== 0 ? 2 : 0} span={11}>
            <Row className="mb-10 mt-10">
              <Col span={8}>
                <Text>{t(`projectsManagement.ProjectForm.info.${item.name}`)}</Text>
              </Col>
              <Col span={16}>
                <div className="boxValue">{item.value ? item.value : "-"}</div>
              </Col>
            </Row>
          </Col>
        ))}
        <Col span={24} className="space-between">
          <Col>
            <Row className="mb-10 mt-10">
              <Col className="mr-2em">
                <Text>{t(`projectsManagement.ProjectForm.info.iva`)}</Text>
              </Col>
              <Col>
                <div className="boxValueCheck">{state.projectData.vat === true ? <CheckOutlined /> : ""}</div>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row className="mb-10 mt-10">
              <Col className="mr-2em">
                <Text>{t(`projectsManagement.ProjectForm.info.withholdingTax`)}</Text>
              </Col>
              <Col>
                <div className="boxValueCheck">{state.projectData.vht === true ? <CheckOutlined /> : ""}</div>
              </Col>
            </Row>
          </Col>
          <Col>
            <Row className="mb-10 mt-10">
              <Col className="mr-2em">
                <Text>{t(`projectsManagement.ProjectForm.info.exportBPC`)}</Text>
              </Col>
              <Col>
                <div className="boxValueCheck">
                  {get(state.projectData, "exportBpc", "") === true ? <CheckOutlined /> : ""}
                </div>
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
      <Collapse items={items} className="mb-1em" activeKey={state.panelActiveKeys} onChange={changeActivePanel} />
    </>
  );
};

export default ProjectsForm;
